'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { clampLanguage, useLanguage } from '@/stores/languageStore';
import {
  fetchTelegramLoginInit,
  loadTelegramLoginScript,
  openTelegramLogin,
  type TelegramLoginInit,
} from '@/utils/telegramLogin';

export function useTelegramLogin(enabled = true) {
  const lang = clampLanguage(useLanguage());
  const [ready, setReady] = useState(false);
  const initRef = useRef<TelegramLoginInit | null>(null);

  const prepare = useCallback(async () => {
    const [init] = await Promise.all([fetchTelegramLoginInit(), loadTelegramLoginScript()]);
    initRef.current = init;
    setReady(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    prepare().catch(() => setReady(false));
  }, [enabled, prepare]);

  const requestIdToken = useCallback(async (): Promise<string> => {
    const init = initRef.current;
    if (!init) throw new Error('telegram_login_not_ready');
    initRef.current = null;
    setReady(false);
    try {
      return await openTelegramLogin(init, lang);
    } finally {
      prepare().catch(() => setReady(false));
    }
  }, [lang, prepare]);

  return { ready, requestIdToken };
}
